import Button from "@components/ui/button";
import { IconX } from "@tabler/icons-react";
import { toast } from "react-toastify";
import {
  EXPORT_FORMAT_LABEL,
  EXPORT_OUTPUT_FORMAT,
  type TExportPdfData,
  type TPdfObj,
} from "../types";
import { downloadPdf } from "../utils";

// Rich "Congrats!" notification fired once, right after a PDF export
// succeeds. Modeled on fathomConnectedToast: a custom react-toastify toast so
// positioning, stacking, dismissal, exit animation and aria-live come from
// the shared ToastContainer instead of a hand-rolled fixed-position card.

// Strip toastify's default card chrome (padding, min-height, background) so
// the toast is just our 348px card. Width/shadow/radius live here since the
// toast wrapper is the outermost element.
const TOAST_CONTAINER_CLASS_NAME =
  "!tw-w-[348px] !tw-min-h-0 !tw-rounded-2xl !tw-p-4 !tw-bg-white tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-base/5 !tw-shadow-md tw-mb-0";
const TOAST_BODY_CLASS_NAME = "!tw-m-0 !tw-p-0";

const PdfReadyToastContent = ({
  pdfData,
  pdfObj,
  openUrl,
}: {
  pdfData: TExportPdfData;
  pdfObj: TPdfObj;
  // Google Slides: the file already lives in Drive, so "open" replaces
  // "download".
  openUrl?: string;
}) => {
  const { version } = pdfData;
  const format = pdfData.format ?? "pdf";
  const formatLabel = EXPORT_FORMAT_LABEL[format];
  // What the download button hands over: a Google Slides export that could
  // not reach Drive is still a PowerPoint file.
  const downloadLabel = EXPORT_FORMAT_LABEL[EXPORT_OUTPUT_FORMAT[format]];
  // Prefer the cover screenshot captured in the same render as the PDF —
  // it shows the actual generated document. The version preview / og image
  // are only fallbacks (the screenshot is best-effort server-side).
  const previewUrl =
    pdfObj.previewUrl ??
    version.previewUrl ??
    (version.data?.ogImage as string | undefined);

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-items-start">
      <h3 className="tw-m-0 tw-font-sans tw-text-lg tw-font-semibold tw-text-blue-base">
        Congrats!
      </h3>
      <p className="tw-m-0 tw-mt-0.5 tw-font-sans tw-text-sm tw-font-normal tw-text-blue-500">
        Your {formatLabel} export is ready.
      </p>
      {!!previewUrl && (
        <div className="tw-mt-4 tw-aspect-[416/226] tw-w-full tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-blue-base/10 tw-bg-white">
          <img
            src={previewUrl}
            alt="Preview of the exported story"
            className="tw-size-full tw-object-cover tw-object-top"
          />
        </div>
      )}
      {openUrl ? (
        <Button
          className="tw-mt-4 tw-w-full tw-gap-1.5"
          onClick={() => window.open(openUrl, "_blank", "noopener")}
        >
          <Button.Icon icon="ri-external-link-line" />
          Open in Google Slides
        </Button>
      ) : (
        <Button
          className="tw-mt-4 tw-w-full tw-gap-1.5"
          onClick={() => void downloadPdf({ url: pdfObj.url, pdfData })}
        >
          <Button.Icon icon="ri-download-2-line" />
          Download {downloadLabel}
        </Button>
      )}
    </div>
  );
};

export const pdfReadyToast = ({
  pdfData,
  pdfObj,
  openUrl,
}: {
  pdfData: TExportPdfData;
  pdfObj: TPdfObj;
  openUrl?: string;
}) =>
  toast(
    <PdfReadyToastContent
      pdfData={pdfData}
      pdfObj={pdfObj}
      openUrl={openUrl}
    />,
    {
      // Unique per export so a fresh export can surface its own toast even if
      // the previous one is still open; the manager's once-guard already
      // prevents double-fires for the same export.
      toastId: `pdf-export-ready-${pdfObj.createdAt}`,
      icon: false,
      position: "bottom-right",
      autoClose: false,
      closeOnClick: false,
      className: TOAST_CONTAINER_CLASS_NAME,
      bodyClassName: TOAST_BODY_CLASS_NAME,
      closeButton: ({ closeToast }) => (
        <button
          type="button"
          aria-label="Dismiss PDF ready notification"
          onClick={closeToast}
          className="tw-absolute tw-right-2 tw-top-3 tw-z-[1] tw-flex tw-size-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-text-blue-400 tw-transition-colors hover:tw-text-blue-base"
        >
          <IconX className="tw-size-4" />
        </button>
      ),
    },
  );
